import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { WorkerLayout } from '../../components/layouts/WorkerLayout';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { FileText, Calendar, Download, CheckCircle2, Camera, Package } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';

const WorkerHistory = () => {
  const { api } = useAuth();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const res = await api.get('/work-reports');
      setReports(res.data);
    } catch (error) {
      toast.error('Error al cargar el historial');
    } finally {
      setLoading(false);
    }
  };

  const downloadPdf = async (reportId) => {
    try {
      const token = localStorage.getItem('token');
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/work-reports/${reportId}/pdf`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await res.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `parte_${reportId.slice(0,8)}.pdf`;
      link.click();
    } catch (error) {
      toast.error('Error al descargar PDF');
    }
  };

  if (loading) {
    return (
      <WorkerLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-600"></div>
        </div>
      </WorkerLayout>
    );
  }

  return (
    <WorkerLayout>
      <div className="p-4 space-y-4" data-testid="worker-history-page">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Historial</h1>
          <p className="text-slate-500">Tus partes de trabajo completados</p>
        </div>

        {reports.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p className="text-slate-500">Aún no tienes partes completados</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {reports.map((report) => (
              <Card key={report.id} className="overflow-hidden">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-lg">{report.client_name}</h3>
                      <p className="text-sm text-slate-500 flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {format(new Date(report.completed_at), "EEEE d 'de' MMMM, yyyy", { locale: es })}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => downloadPdf(report.id)}
                      data-testid={`download-pdf-${report.id}`}
                    >
                      <Download className="w-4 h-4 mr-1" />
                      PDF
                    </Button>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <Badge variant="outline" className="flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3 text-green-500" />
                      {report.checklist?.filter(c => c.checked).length || 0}/{report.checklist?.length || 0} tareas
                    </Badge>
                    {report.parameters?.length > 0 && (
                      <Badge variant="outline">
                        {report.parameters.length} parámetros
                      </Badge>
                    )}
                    {report.photos?.length > 0 && (
                      <Badge variant="outline" className="flex items-center gap-1">
                        <Camera className="w-3 h-3" />
                        {report.photos.length} fotos
                      </Badge>
                    )}
                    {report.materials_used?.length > 0 && (
                      <Badge variant="outline" className="flex items-center gap-1">
                        <Package className="w-3 h-3" />
                        {report.materials_used.length} materiales
                      </Badge>
                    )}
                  </div>

                  {report.observations && (
                    <p className="mt-3 text-sm text-slate-600 bg-slate-50 p-3 rounded-lg">
                      {report.observations}
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </WorkerLayout>
  );
};

export default WorkerHistory;
